import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { useCallback } from 'react';
import { loginRequest } from './msalConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginHint } from './loginHint';

export interface AuthInfo {
    isAuthenticated: boolean;
    name: string;
    email: string;
    /** Get a fresh access token. Silently refreshes; falls back to redirect if needed. */
    getToken: () => Promise<string>;
    signIn: () => void;
    signOut: () => void;
}

export function useAuth(): AuthInfo {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] ?? null);
    const isAuthenticated = useIsAuthenticated();

    const getToken = useCallback(async (): Promise<string> => {
        if (!account) {
            throw new Error('Not signed in.');
        }
        try {
            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account,
            });
            return response.accessToken;
        } catch (err) {
            if (err instanceof InteractionRequiredAuthError) {
                // Silent refresh failed — likely token expired beyond refresh window.
                // Trigger an interactive redirect, which will return us to the same URL.
                await instance.acquireTokenRedirect({ ...loginRequest, loginHint: account.username || loginHint() });
                throw err;
            }
            throw err;
        }
    }, [instance, account]);

    const signIn = useCallback(() => {
        // F809: the account the WMS link named (if any) is offered first
        instance.loginRedirect({ ...loginRequest, ...(loginHint() ? { loginHint: loginHint() } : {}) }).catch((err) => {
            console.error('[auth] loginRedirect failed:', err);
        });
    }, [instance]);

    const signOut = useCallback(() => {
        instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
        }).catch((err) => {
            console.error('[auth] logoutRedirect failed:', err);
        });
    }, [instance]);

    return {
        isAuthenticated,
        name: account?.name ?? '',
        email: account?.username ?? '',
        getToken,
        signIn,
        signOut,
    };
}
